import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Close, ContentCopyOutlined, EventAvailableOutlined, LinkOffOutlined, LinkOutlined, OpenInNewOutlined, Refresh, SaveOutlined } from '@mui/icons-material';
import { createInvitation, planningError, revokeInvitation, updateInvitationDeadline } from '../actions/planning';

const localInput = value => {
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export default function GuestInvitationDialog({ guest, onClose, onChanged }) {
  const [limit, setLimit] = useState('0');
  const [url, setUrl] = useState('');
  const [expires, setExpires] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [deadline, setDeadline] = useState('');
  const [rsvpWindow, setRsvpWindow] = useState('24h');
  const [hasInvitation, setHasInvitation] = useState(false);
  useEffect(() => {
    setLimit(String(guest?.invitation?.max_plus_ones ?? Math.min(guest?.plus_one || 0, 50)));
    setHasInvitation(Boolean(guest?.invitation));
    setExpires(guest?.invitation?.expires_at || '');
    setDeadline(localInput(guest?.invitation?.expires_at || Date.now() + 86400000));
    setRsvpWindow(guest?.invitation ? 'custom' : '24h');
    setUrl('');
    setError('');
    setMessage('');
  }, [guest]);
  const expiresAt = new Date(deadline);
  const validDeadline = deadline !== '' && Number.isFinite(expiresAt.getTime()) && expiresAt > new Date();
  const changeWindow = value => {
    setRsvpWindow(value);
    if (value !== 'custom') setDeadline(localInput(Date.now() + (value === '24h' ? 1 : 7) * 86400000));
  };
  const deadlineTimestamp = () => rsvpWindow === 'custom' ? expiresAt.toISOString() :
    new Date(Date.now() + (rsvpWindow === '24h' ? 1 : 7) * 86400000).toISOString();
  const generate = async () => {
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await createInvitation(guest.guest_event_id, Number(limit), deadlineTimestamp());
      setUrl(`${window.location.origin}/invite#token=${result.token}`);
      setExpires(result.expires_at);
      setHasInvitation(true);
      onChanged?.({ expires_at: result.expires_at, max_plus_ones: result.max_plus_ones, responded_at: null });
    } catch (reason) { setError(planningError(reason)); }
    finally { setBusy(false); }
  };
  const saveDeadline = async () => {
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await updateInvitationDeadline(guest.guest_event_id, deadlineTimestamp());
      setExpires(result.expires_at);
      onChanged?.({ expires_at: result.expires_at });
      setMessage('RSVP deadline updated. The current link and saved response are unchanged.');
    } catch (reason) { setError(planningError(reason)); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true); setError('');
    try { await revokeInvitation(guest.guest_event_id); setUrl(''); setExpires(''); setHasInvitation(false); onChanged?.(null); setMessage('The private link is revoked. Existing responses are preserved.'); }
    catch (reason) { setError(planningError(reason)); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setMessage('Private invitation link copied.'); }
    catch { setError('Clipboard access is unavailable. Select and copy the link below.'); }
  };
  return (
    <Dialog open={Boolean(guest)} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="guest-invitation-title" slotProps={{ paper: { sx: { borderRadius: 4 } } }}>
      <DialogTitle id="guest-invitation-header" sx={{ p: 3, pb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ display: 'flex', p: 1.25, borderRadius: 3, bgcolor: 'action.selected', color: 'primary.main' }}><LinkOutlined /></Box>
          <Box id="guest-invitation-title" aria-label={`Private invitation for ${guest?.name}`} sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="span" variant="overline" color="text.secondary">Private invitation for</Typography>{' '}
            <Typography component="span" variant="h6" sx={{ display: 'block', fontWeight: 700, overflowWrap: 'anywhere' }}>{guest?.name}</Typography>
          </Box>
          <Tooltip title="Close" arrow><span><IconButton aria-label="Close" disabled={busy} onClick={onClose}><Close /></IconButton></span></Tooltip>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Manage this household's RSVP window and private link. Owner only; nothing is sent automatically.</Typography>
        <Box sx={{ p: { xs: 2, sm: 2.5 }, border: 1, borderColor: 'divider', borderRadius: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
          <EventAvailableOutlined color="primary" fontSize="small" /><Typography variant="subtitle2" fontWeight={700}>RSVP settings</Typography>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <TextField fullWidth size="small" disabled={busy} type="number" label="Maximum additional guests" value={limit} onChange={e => setLimit(e.target.value)} inputProps={{ min: 0, max: 50, step: 1 }} />
          <TextField select size="small" label="RSVP window" value={rsvpWindow} onChange={event => changeWindow(event.target.value)} disabled={busy}>
            <MenuItem value="24h">1 day</MenuItem>
            <MenuItem value="7d">7 days</MenuItem>
            <MenuItem value="custom">Custom deadline</MenuItem>
          </TextField>
        </Box>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField fullWidth size="small" label="RSVP deadline" type="datetime-local" value={deadline} onChange={event => { setDeadline(event.target.value); setRsvpWindow('custom'); }} disabled={busy} slotProps={{ inputLabel: { shrink: true } }} helperText={`Local time (${Intl.DateTimeFormat().resolvedOptions().timeZone}). The link closes after this deadline.`} />
            {hasInvitation && <Tooltip title="Save RSVP deadline" arrow><span><IconButton aria-label="Save RSVP deadline" color="primary" disabled={busy || !validDeadline} onClick={saveDeadline}><SaveOutlined /></IconButton></span></Tooltip>}
          </Stack>
          {expires && <Typography variant="caption" color="text.secondary">Current RSVP deadline: {new Date(expires).toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</Typography>}
        </Stack>
        </Box>
        {url && <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'action.hover' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>Ready to share</Typography>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Copy invitation link" arrow><IconButton aria-label="Copy invitation link" color="primary" onClick={copy}><ContentCopyOutlined fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Preview invitation" arrow><IconButton aria-label="Preview invitation" component="a" href={url} target="_blank" rel="noopener noreferrer" color="primary"><OpenInNewOutlined fontSize="small" /></IconButton></Tooltip>
            </Stack>
          </Stack>
          <TextField fullWidth size="small" label="Private invitation link" value={url} slotProps={{ input: { readOnly: true, sx: { fontSize: '0.8rem' } } }} onFocus={e => e.target.select()} />
        </Box>}
        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}><Typography variant="body2">Share privately: anyone with the link can respond until the deadline. Saving a deadline keeps the link and response. Replacing the link invalidates it and requests a fresh response.</Typography></Alert>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: 1, borderColor: 'divider' }}>
        <Chip size="small" variant="outlined" label={hasInvitation ? 'Link created' : 'No private link'} sx={{ mr: 'auto' }} />
        {busy && <CircularProgress size={20} aria-label="Updating invitation" />}
        <Tooltip title="Revoke link" arrow><span><IconButton aria-label="Revoke link" color="error" disabled={busy || !hasInvitation} onClick={revoke}><LinkOffOutlined /></IconButton></span></Tooltip>
        <Tooltip title={hasInvitation ? 'Replace link' : 'Create link'} arrow><span>
          <IconButton aria-label={hasInvitation ? 'Replace link' : 'Create link'} color="primary" sx={{ bgcolor: 'action.selected', borderRadius: 2 }} disabled={busy || !validDeadline || limit.trim() === '' || !Number.isInteger(Number(limit)) || Number(limit) < 0 || Number(limit) > 50} onClick={generate}>
            {hasInvitation ? <Refresh /> : <LinkOutlined />}
          </IconButton>
        </span></Tooltip>
      </DialogActions>
    </Dialog>
  );
}
